/**
 * lib/recurring.ts
 * ------------------------------------------------------------------
 * Entries that repeat, and costs that belong to a month other than the one
 * they were paid in.
 *
 * PURE. Templates and schedules in, journal lines out. No Firestore, no
 * clock passed implicitly — every function that needs "now" takes it as an
 * argument, because a month-end routine that behaves differently depending
 * on when it happens to run is a routine nobody can check.
 *
 * TWO THINGS LIVE HERE, and they are close enough to share a calendar but
 * not close enough to share a type.
 *
 * A RECURRING JOURNAL is the same entry every month: the rent, the security
 * contract, the depreciation charge someone works out once and posts twelve
 * times. It has fixed lines and no end to what it accumulates — it simply
 * repeats until the school stops it.
 *
 * A DEFERRAL is one amount spread across months. The school pays a year of
 * insurance in January; the cost belongs to twelve months, not to January.
 * Recording it as January expense makes January look terrible and the other
 * eleven look better than they were, and the accounts stop describing how
 * the school actually runs. So the payment goes to Prepayments (an asset —
 * something bought and not yet consumed) and a twelfth is released to the
 * expense each month. An ACCRUAL is the mirror: something consumed but not
 * yet billed, sitting in Accruals (a liability) until the invoice arrives.
 *
 * THE ROUNDING REMAINDER GOES ON THE LAST MONTH, not spread and not
 * dropped. P1,000 over three months is 333.33, 333.33, 333.34 — the account
 * empties exactly, and a prepayment that never quite reaches zero is a
 * balance nobody can explain and nobody can clear.
 *
 * EVERY GENERATED JOURNAL IS IDEMPOTENT BY ID. The source id carries the
 * template and the period, so running the month-end sweep twice — or three
 * times, because a scheduled function will be retried — posts once.
 * ------------------------------------------------------------------ */
import { round2 } from '@/lib/financeCore';
import type { JournalLine } from '@/lib/gl';

/* ============================== calendar =============================== */

/** '2026-08-14' -> '2026-08'. A period is a month; nothing here is finer. */
export function monthOf(dateIso: string): string {
  return String(dateIso ?? '').slice(0, 7);
}

/** '2026-11' + 2 -> '2026-13' is wrong; this rolls the year properly. */
export function addMonths(period: string, n: number): string {
  const [y, m] = String(period).split('-').map(Number);
  if (!y || !m) return period;
  const zero = y * 12 + (m - 1) + n;
  return `${Math.floor(zero / 12)}-${String((zero % 12) + 1).padStart(2, '0')}`;
}

/** Whole months from a to b. Negative if b is earlier. */
export function monthsBetween(a: string, b: string): number {
  const [ay, am] = String(a).split('-').map(Number);
  const [by, bm] = String(b).split('-').map(Number);
  if (!ay || !am || !by || !bm) return 0;
  return (by * 12 + bm) - (ay * 12 + am);
}

/**
 * The day a posting in this month should carry.
 *
 * THE LAST DAY OF THE MONTH, always. A recurring charge belongs to the month
 * as a whole, and dating it the 1st puts it before the events it pays for
 * while dating it the 31st in February does not exist. The last day is the
 * only choice that is both inside the period and real for every month.
 */
export function periodEndDate(period: string): string {
  const [y, m] = String(period).split('-').map(Number);
  if (!y || !m) return '';
  return new Date(Date.UTC(y, m, 0)).toISOString().slice(0, 10);
}

/** Every month from `from` to `to` inclusive. Empty if `to` precedes `from`. */
export function monthsFrom(from: string, to: string): string[] {
  const n = monthsBetween(from, to);
  if (n < 0) return [];
  return Array.from({ length: n + 1 }, (_, i) => addMonths(from, i));
}

/* ========================= recurring journals ========================== */

export interface RecurringTemplate {
  id: string;
  /** What it is, in the words the bursar would use. Becomes the narration. */
  name: string;
  /** The entry, exactly as it posts. Must balance — validated before saving. */
  lines: JournalLine[];
  /** First month it posts, inclusive. '2026-09'. */
  startPeriod: string;
  /** Last month it posts, inclusive. Absent = until stopped. */
  endPeriod?: string;
  active: boolean;
  createdAt: string;
  createdBy?: string;
  /** Set when the school stops it. Never deleted — what it posted stands. */
  stoppedAt?: string;
  stoppedBy?: string;
  stoppedReason?: string;
}

/** One journal a schedule says is owed. */
export interface DueEntry {
  sourceId: string;
  period: string;
  date: string;
  narration: string;
  lines: JournalLine[];
}

/**
 * The source id for a recurring posting.
 *
 * Template AND period, so the same template posts once per month and the
 * month-end sweep can be run as often as anyone likes.
 */
export function recurringSourceId(templateId: string, period: string): string {
  return `rec-${templateId}-${period}`;
}

/**
 * Why this template cannot be saved, or null if it can.
 *
 * A template is checked HARDER than a one-off journal, because it will post
 * unattended every month and nobody reads a journal that arrived on its own.
 */
export function recurringProblem(t: Pick<RecurringTemplate, 'name' | 'lines' | 'startPeriod' | 'endPeriod'>): string | null {
  if (!t.name?.trim()) return 'Give it a name — it is what will appear on twelve journals.';
  if (!/^\d{4}-\d{2}$/.test(t.startPeriod ?? '')) return 'Give it a month to start from.';
  if (t.endPeriod) {
    if (!/^\d{4}-\d{2}$/.test(t.endPeriod)) return 'That end month is not a month.';
    if (monthsBetween(t.startPeriod, t.endPeriod) < 0) return 'It cannot end before it starts.';
  }

  const lines = (t.lines ?? []).filter((l) => (l.dr || 0) !== 0 || (l.cr || 0) !== 0);
  if (lines.length < 2) return 'A journal needs at least two lines.';
  if (lines.some((l) => !l.accountCode)) return 'Every line needs an account.';
  if (lines.some((l) => (l.dr || 0) < 0 || (l.cr || 0) < 0)) {
    return 'A line is a debit or a credit, never a negative of the other.';
  }
  if (lines.some((l) => (l.dr || 0) !== 0 && (l.cr || 0) !== 0)) {
    return 'A line is a debit or a credit, not both.';
  }

  const dr = round2(lines.reduce((a, l) => a + (l.dr || 0), 0));
  const cr = round2(lines.reduce((a, l) => a + (l.cr || 0), 0));
  if (dr !== cr) {
    return `It does not balance — debits ${dr.toFixed(2)}, credits ${cr.toFixed(2)}. `
      + 'An unbalanced template would fail silently every month.';
  }
  if (dr === 0) return 'It comes to nothing.';
  return null;
}

/**
 * What this template owes, up to and including `upTo`.
 *
 * `alreadyPosted` is the set of source ids already in the ledger, so a
 * sweep that has run before returns only the gap. It is passed in rather
 * than queried here to keep this pure — and because the caller reading the
 * journals once beats this reading them per template.
 *
 * A STOPPED TEMPLATE STILL OWES ITS PAST. Stopping it ends future postings;
 * it does not retract months that had already fallen due and were missed.
 * Silently swallowing those would leave a gap in the accounts that only
 * shows up as expenses reading low.
 */
export function recurringDue(
  t: RecurringTemplate,
  upTo: string,
  alreadyPosted: ReadonlySet<string>,
): DueEntry[] {
  const last = t.endPeriod && monthsBetween(t.endPeriod, upTo) > 0 ? t.endPeriod : upTo;
  return monthsFrom(t.startPeriod, last)
    .filter((p) => !alreadyPosted.has(recurringSourceId(t.id, p)))
    .map((p) => ({
      sourceId: recurringSourceId(t.id, p),
      period: p,
      date: periodEndDate(p),
      narration: `${t.name} — ${p}`,
      lines: t.lines,
    }));
}

/* ============================== deferrals ============================== */

export type DeferralKind = 'prepayment' | 'accrual';

export interface Deferral {
  id: string;
  kind: DeferralKind;
  /** What it is. 'Insurance — 2026 policy year'. */
  description: string;
  /** The whole amount, positive. Direction comes from the kind. */
  amount: number;
  /** The expense (or income) account the months are charged to. */
  accountCode: string;
  /** First month it is charged to. */
  startPeriod: string;
  /** How many months it spreads over. At least 1. */
  months: number;
  /** Date the underlying payment or obligation was recorded. */
  recognisedOn: string;
  active: boolean;
  createdAt: string;
  createdBy?: string;
  releasedEarlyAt?: string;
  releasedEarlyReason?: string;
}

export function deferralSourceId(deferralId: string, period: string): string {
  return `def-${deferralId}-${period}`;
}

/** The month after the last one this deferral charges. */
export function deferralEndPeriod(d: Pick<Deferral, 'startPeriod' | 'months'>): string {
  return addMonths(d.startPeriod, Math.max(1, d.months) - 1);
}

export function deferralProblem(d: Pick<Deferral, 'description' | 'amount' | 'accountCode' | 'startPeriod' | 'months'>): string | null {
  if (!d.description?.trim()) return 'Say what it is — a balance in Prepayments with no description is one nobody can clear.';
  if (!d.accountCode) return 'Choose the account the months are charged to.';
  if (!/^\d{4}-\d{2}$/.test(d.startPeriod ?? '')) return 'Give it a month to start from.';
  if (!Number.isInteger(d.months) || d.months < 1) return 'It spreads over a whole number of months, at least one.';
  if (d.months > 60) return 'More than five years is almost certainly a typo.';
  const amt = round2(Number(d.amount) || 0);
  if (!(amt > 0)) {
    return 'The amount is positive. Which way it goes comes from whether it is a prepayment or an accrual, never from a sign.';
  }
  if (round2(amt / d.months) < 0.01) return 'Spread that thin, some months would charge nothing.';
  return null;
}

/**
 * The monthly instalments.
 *
 * Even to the cent, with the REMAINDER ON THE LAST MONTH. Spreading it would
 * make eleven months differ from each other for no reason a reader could
 * follow; dropping it would leave the prepayment account holding three cents
 * forever, which is the kind of balance that outlives everyone who knows
 * what it was.
 */
export function deferralInstalments(amount: number, months: number): number[] {
  const n = Math.max(1, Math.floor(months));
  const total = round2(amount);
  const each = round2(Math.floor((total * 100) / n) / 100);
  const out = Array.from({ length: n }, () => each);
  out[n - 1] = round2(total - each * (n - 1));
  return out;
}

export interface DeferralAccounts {
  /** PREPAYMENTS — an asset. Something bought and not yet consumed. */
  prepayments: string;
  /** ACCRUALS — a liability. Something consumed and not yet billed. */
  accruals: string;
}

/**
 * The entry that sets the deferral up.
 *
 * A PREPAYMENT moves a cost already recorded OUT of the expense and into the
 * asset: Dr Prepayments, Cr the expense. It assumes the payment itself was
 * already posted to that expense, which is what a voucher or a payment does.
 *
 * AN ACCRUAL is the opposite and does not assume anything was posted: the
 * school has consumed something nobody has billed, so Dr the expense, Cr
 * Accruals, and the liability sits there until the invoice arrives.
 */
export function deferralRecognitionLines(d: Deferral, accounts: DeferralAccounts): JournalLine[] {
  const amount = round2(d.amount);
  const holding = d.kind === 'prepayment' ? accounts.prepayments : accounts.accruals;

  return d.kind === 'prepayment'
    ? [
      { accountCode: holding, dr: amount, cr: 0, narration: `${d.description} — carried forward` },
      { accountCode: d.accountCode, dr: 0, cr: amount, narration: `${d.description} — deferred` },
    ]
    : [
      { accountCode: d.accountCode, dr: amount, cr: 0, narration: `${d.description} — accrued` },
      { accountCode: holding, dr: 0, cr: amount, narration: `${d.description} — accrued` },
    ];
}

/**
 * One month's release.
 *
 * A prepayment releases the other way from how it was set up — the asset
 * empties into the expense a month at a time. An accrual releases by
 * unwinding the liability, which is what happens when the bill finally
 * arrives and is posted normally.
 */
export function deferralReleaseLines(
  d: Deferral,
  amount: number,
  period: string,
  accounts: DeferralAccounts,
): JournalLine[] {
  const holding = d.kind === 'prepayment' ? accounts.prepayments : accounts.accruals;
  const amt = round2(amount);

  return d.kind === 'prepayment'
    ? [
      { accountCode: d.accountCode, dr: amt, cr: 0, narration: `${d.description} — ${period}` },
      { accountCode: holding, dr: 0, cr: amt, narration: `${d.description} — released ${period}` },
    ]
    : [
      { accountCode: holding, dr: amt, cr: 0, narration: `${d.description} — released ${period}` },
      { accountCode: d.accountCode, dr: 0, cr: amt, narration: `${d.description} — ${period}` },
    ];
}

/** What this deferral owes, up to and including `upTo`. */
export function deferralDue(
  d: Deferral,
  upTo: string,
  alreadyPosted: ReadonlySet<string>,
  accounts: DeferralAccounts,
): DueEntry[] {
  const instalments = deferralInstalments(d.amount, d.months);
  const out: DueEntry[] = [];

  instalments.forEach((amount, i) => {
    const period = addMonths(d.startPeriod, i);
    if (monthsBetween(period, upTo) < 0) return;
    const sourceId = deferralSourceId(d.id, period);
    if (alreadyPosted.has(sourceId)) return;
    out.push({
      sourceId,
      period,
      date: periodEndDate(period),
      narration: `${d.description} — ${period}`,
      lines: deferralReleaseLines(d, amount, period, accounts),
    });
  });

  return out;
}

/**
 * How much of a deferral has not yet been released.
 *
 * Derived from what IS posted rather than from a stored counter, for the
 * same reason ledger balances are computed rather than stored: a counter and
 * the journals it claims to describe drift, and the counter is the one that
 * lies convincingly.
 */
export function deferralOutstanding(d: Deferral, postedSourceIds: ReadonlySet<string>): number {
  const instalments = deferralInstalments(d.amount, d.months);
  let released = 0;
  instalments.forEach((amount, i) => {
    if (postedSourceIds.has(deferralSourceId(d.id, addMonths(d.startPeriod, i)))) released += amount;
  });
  return round2(round2(d.amount) - round2(released));
}
